import React, { useState, useEffect } from 'react';
import { getVideoUrl } from '../config/cdn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPhone, 
  faEnvelope, 
  faMapMarkerAlt, 
  faClock, 
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram,
  faFacebook,
  faTiktok,
  faYoutube
} from '@fortawesome/free-brands-svg-icons';

const Contacto = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    website: ''  // Honeypot field
  });
  
  // Anti-spam: Track form load time
  const [formLoadTime, setFormLoadTime] = useState(Date.now());
  
  // Form submission states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');
  
  const [openFAQ, setOpenFAQ] = useState(null);
  
  // Estados para el formulario de suscripción
  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');

  // Set form load time on component mount
  useEffect(() => {
    setFormLoadTime(Date.now());
  }, []);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setSubmitMessage('');
    setSubmitError('');
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      setSubmitError('Por favor completa todos los campos');
      return;
    }
    
    // Calculate time to fill (for anti-spam)
    const timeToFill = Date.now() - formLoadTime;
    
    setIsSubmitting(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      const response = await fetch(`${backendUrl}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          website: formData.website,  // Honeypot field
          time_to_fill: timeToFill
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSubmitMessage(data.message || '¡Mensaje enviado exitosamente! Te contactaremos pronto.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          website: ''
        });
        // Reset form load time
        setFormLoadTime(Date.now());
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSubmitMessage('');
        }, 5000);
      } else {
        setSubmitError(data.detail || 'Error al enviar el mensaje. Por favor, intenta nuevamente.');
      }
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Error de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Función para manejar la suscripción
  const handleSubscription = async (e) => {
    e.preventDefault();
    
    if (!subscriptionEmail) {
      setSubscriptionMessage('Por favor ingresa tu email');
      return;
    }

    if (!subscriptionEmail.includes('@')) {
      setSubscriptionMessage('Por favor ingresa un email válido');
      return;
    }

    setIsSubscribing(true);
    
    // Simular proceso de suscripción
    setTimeout(() => {
      setSubscriptionMessage('¡Gracias por suscribirte! Te mantendremos informado sobre novedades y eventos.');
      setSubscriptionEmail('');
      setIsSubscribing(false);
      
      // Limpiar mensaje después de 5 segundos
      setTimeout(() => {
        setSubscriptionMessage('');
      }, 5000);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32">
        {/* Background Video */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src={getVideoUrl("hero de pagina de contactos.mp4")} type="video/mp4" />
          </video>
          {/* Dark overlay for text readability */}
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="container-custom max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center">
            <h1 className="font-sans text-5xl md:text-6xl font-light text-white leading-tight mb-6">
              Hablemos de tu próxima 
              <span className="text-green-400 font-normal italic"> Experiencia culinaria</span>
            </h1>
            
            <p className="font-sans text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
              ¿Tienes preguntas, comentarios o deseas hacer una reserva especial? 
              Estamos aquí para ayudarte a crear momentos inolvidables.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="relative py-20 bg-white overflow-hidden">
        <div className="container-custom max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-sans text-5xl md:text-6xl font-light text-gray-900 leading-tight mb-6">
              Contáctanos
            </h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-green-600 to-transparent mx-auto mb-6"></div>
            <p className="font-sans text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Estamos aquí para hacer tu experiencia inolvidable. Envíanos un mensaje y nos pondremos en contacto contigo pronto.
            </p>
          </div>

          {/* Map and Form Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Map Section - Left */}
            <div className="relative z-10">
              <div className="bg-white/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-200 shadow-xl h-full">
                <h3 className="font-sans text-2xl font-semibold text-gray-900 mb-4">
                  Encuéntranos
                </h3>
                <p className="text-gray-600 mb-6">
                  Estamos ubicados en el corazón de La Habana Vieja, en la calle más comercial de Cuba, Obispo.
                </p>
                
                {/* Map Placeholder */}
                <div className="w-full h-96 bg-gray-200 rounded-xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3668.858383003836!2d-82.35497672394031!3d23.138851111977583!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88cd790029e54ff7%3A0x2322fdb9935a7d42!2sAdastra%20Bar-Restaurant!5e0!3m2!1sen!2sus!4v1760575753836!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="ADASTRA Bar-Restaurant Location"
                  ></iframe>
                </div>
                
                <div className="mt-6 space-y-3">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="text-black text-xl" />
                    <span className="text-gray-700">Obispo 255 entre Cuba y Aguiar, La Habana Vieja</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faPhone} className="text-black text-xl" />
                    <span className="text-gray-700">+53 6351 8498</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faClock} className="text-black text-xl" />
                    <span className="text-gray-700">Todos los días: 12:00 PM - 12:00 AM</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form - Right */}
            <div className="relative z-10">
              <form onSubmit={handleSubmit} className="space-y-6 bg-white/50 backdrop-blur-sm p-8 rounded-2xl border border-gray-200 shadow-xl h-full">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-black font-medium mb-3">
                      Nombre Completo *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                      placeholder="Juan Pérez"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-black font-medium mb-3">
                      Teléfono
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                      placeholder="+53 1234 5678"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-black font-medium mb-3">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                    placeholder="tu@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-black font-medium mb-3">
                    Asunto
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black focus:ring-2 focus:ring-black/50 focus:border-black transition-all shadow-sm"
                  >
                    <option value="" className="bg-white">Selecciona un asunto</option>
                    <option value="reserva" className="bg-white">Reserva especial</option>
                    <option value="evento" className="bg-white">Evento privado</option>
                    <option value="menu" className="bg-white">Consulta sobre menú</option>
                    <option value="comisiones" className="bg-white">Comisiones</option>
                    <option value="grupos_grandes" className="bg-white">Ofertas para Grupos Grandes</option>
                    <option value="feedback" className="bg-white">Comentarios</option>
                    <option value="otro" className="bg-white">Otro</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-black font-medium mb-3">
                    Mensaje *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 bg-white backdrop-blur-sm border-2 border-gray-300 rounded-xl text-black placeholder-gray-500 focus:ring-2 focus:ring-black/50 focus:border-black transition-all resize-none shadow-sm"
                    placeholder="Cuéntanos cómo podemos ayudarte..."
                  ></textarea>
                </div>

                {/* Honeypot field - hidden from users, only bots will fill it */}
                <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
                  <label htmlFor="website">Website (do not fill)</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                </div>

                {/* Success Message */}
                {submitMessage && (
                  <div className="p-4 bg-green-50 border-2 border-green-500 rounded-xl text-green-800 font-medium">
                    ✅ {submitMessage}
                  </div>
                )}

                {/* Error Message */}
                {submitError && (
                  <div className="p-4 bg-red-50 border-2 border-red-500 rounded-xl text-red-800 font-medium">
                    ❌ {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-black text-white py-4 px-8 rounded-xl font-medium hover:bg-gray-800 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transform hover:scale-105 shadow-lg"
                >
                  <FontAwesomeIcon icon={faPaperPlane} className="w-5 h-5" />
                  <span>{isSubmitting ? 'Enviando...' : 'Enviar Mensaje'}</span>
                </button>
              </form>
            </div>
          </div>

          {/* Contact Information - Bottom (Solo 2 cards) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-center max-w-3xl mx-auto">
            <div className="p-6">
              <FontAwesomeIcon icon={faPhone} className="text-black text-3xl mb-3" />
              <h3 className="font-semibold text-lg mb-2">Teléfono</h3>
              <p className="text-gray-600">+53 6351 8498</p>
            </div>
            <div className="p-6">
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-black text-3xl mb-3" />
              <h3 className="font-semibold text-lg mb-2">Ubicación</h3>
              <p className="text-gray-600">Obispo 255 entre Cuba y Aguiar, La Habana Vieja</p>
            </div>
          </div>
        </div>
      </section>
      
    </div>
  );
};

export default Contacto;